/** Browser upload progress is measured from bytes sent, never from a timer. */
export async function transferUpload(url: string, init: RequestInit, onProgress?: (fraction: number) => void): Promise<Response> {
  init.signal?.throwIfAborted()
  if (typeof XMLHttpRequest === 'undefined') {
    const response = await fetch(url, init)
    if (response.ok) onProgress?.(1)
    return response
  }
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const signal = init.signal
    const abort = () => xhr.abort()
    const cleanup = () => signal?.removeEventListener('abort', abort)
    xhr.open(init.method || 'POST', url)
    xhr.withCredentials = init.credentials === 'include'
    new Headers(init.headers).forEach((value, name) => xhr.setRequestHeader(name, value))
    xhr.upload.onprogress = (event) => { if (event.lengthComputable && event.total) onProgress?.(Math.min(1, event.loaded / event.total)) }
    // Once a local form reaches the server, await its commit response. The UI
    // switches to Finalizing and disables Cancel at this same boundary.
    if (init.body instanceof FormData) xhr.upload.onload = () => { cleanup(); onProgress?.(1) }
    xhr.onload = () => {
      cleanup()
      resolve(new Response(xhr.responseText || null, { status: xhr.status, headers: xhr.getAllResponseHeaders().trim().split(/[\r\n]+/).filter(Boolean).map((line) => {
        const index = line.indexOf(':')
        return [line.slice(0, index).trim(), line.slice(index + 1).trim()]
      }) }))
    }
    xhr.onerror = () => { cleanup(); reject(new TypeError('The upload connection was interrupted. Select the same file to resume.')) }
    xhr.onabort = () => { cleanup(); reject(signal?.reason || new DOMException('Upload cancelled.', 'AbortError')) }
    signal?.addEventListener('abort', abort, { once: true })
    if (signal?.aborted) { cleanup(); reject(signal.reason); return }
    try { xhr.send(init.body as XMLHttpRequestBodyInit) }
    catch (error) { cleanup(); reject(error) }
  })
}

export function uploadBytes(fraction: number | null, total: number) {
  const format = (bytes: number) => bytes >= 1024 ** 3 ? `${(bytes / 1024 ** 3).toFixed(2)} GB` : `${(bytes / 1024 ** 2).toFixed(1)} MB`
  return `${format(Math.round((fraction || 0) * total))} of ${format(total)}`
}
